import type { ThreadMessageLike } from "@assistant-ui/react";
import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import type { StreamEvent } from "../api/ai-agent-client";

interface AiChatState {
  messages: ThreadMessageLike[];
  isRunning: boolean;
  threadId: string;
  suggestions: string[];
}

interface AiChatActions {
  handleStreamEvent: (event: StreamEvent) => void;
  addUserMessage: (message: ThreadMessageLike) => void;
  addErrorMessage: (text: string) => void;
  setMessages: (messages: ThreadMessageLike[]) => void;
  setIsRunning: (running: boolean) => void;
  setThreadId: (id: string) => void;
  setSuggestions: (suggestions: string[]) => void;
  resetThread: () => void;
}

export type AiChatStore = AiChatState & AiChatActions;

/**
 * Zustand store for AI chat state management
 *
 * This is the single source of truth for all chat-related state.
 */
export const useAiChatStore = create<AiChatStore>((set) => ({
  // Initial state
  messages: [],
  isRunning: false,
  threadId: uuidv4(),
  suggestions: [],

  // Actions
  handleStreamEvent: (event: StreamEvent) => {
    set((state) => {
      // Handle text delta events (streaming tokens)
      if (event.type === "text_delta") {
        const existingIndex = state.messages.findIndex((m) => m.id === event.data.id);

        if (existingIndex === -1) {
          // First token - create new message
          return {
            messages: [
              ...state.messages,
              {
                id: event.data.id,
                role: "assistant" as const,
                content: [{ type: "text" as const, text: event.data.delta }],
                createdAt: new Date(),
              },
            ],
          };
        }

        // Append to existing message
        const updated = [...state.messages];
        const message = { ...updated[existingIndex] };
        const content = message.content;

        if (typeof content !== "string" && Array.isArray(content)) {
          const textContent = content.find((c) => c.type === "text");

          if (textContent && textContent.type === "text") {
            // Update the text content immutably
            const updatedContent = content.map((c) =>
              c.type === "text" ? { ...c, text: c.text + event.data.delta } : c
            );
            message.content = updatedContent;
          }
        }

        updated[existingIndex] = message;
        return { messages: updated };
      }

      // Handle tool results by updating existing tool call message
      if (event.type === "tool_result") {
        let foundMessageIndex = -1;
        let foundContentIndex = -1;

        for (let i = state.messages.length - 1; i >= 0; i--) {
          const msg = state.messages[i];
          if (msg.role !== "assistant") continue;
          const content = msg.content;
          if (typeof content === "string") continue;

          const contentIndex = content.findIndex(
            (c) => c.type === "tool-call" && c.toolCallId === event.data.id
          );

          if (contentIndex !== -1) {
            foundMessageIndex = i;
            foundContentIndex = contentIndex;
            break;
          }
        }

        if (foundMessageIndex === -1) return state;

        const updatedMessages = [...state.messages];
        const message = { ...updatedMessages[foundMessageIndex] };
        const content = message.content;

        if (typeof content !== "string") {
          const updatedContent = [...content];
          const existingContent = updatedContent[foundContentIndex];
          if (existingContent.type === "tool-call") {
            updatedContent[foundContentIndex] = {
              ...existingContent,
              result: event.data.result,
            } as any;
          }
          message.content = updatedContent;
        }

        updatedMessages[foundMessageIndex] = message;
        return { messages: updatedMessages };
      }

      // Handle tool_call events
      if (event.type === "tool_call") {
        const toolCallExists = state.messages.some((msg) => {
          if (typeof msg.content === "string") return false;
          return msg.content.some((c) => c.type === "tool-call" && c.toolCallId === event.data.id);
        });

        if (toolCallExists) return state;

        const lastMessage = state.messages[state.messages.length - 1];
        const isToolCallMessage =
          lastMessage?.role === "assistant" &&
          typeof lastMessage.content !== "string" &&
          lastMessage.content.some((c) => c.type === "tool-call");

        if (isToolCallMessage && typeof lastMessage.content !== "string") {
          const updatedMessages = [...state.messages];
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            content: [
              ...lastMessage.content,
              {
                type: "tool-call" as const,
                toolCallId: event.data.id,
                toolName: event.data.tool,
                args: event.data.args,
                argsText: JSON.stringify(event.data.args),
              },
            ],
          };
          return { messages: updatedMessages };
        }

        // Create new message from event
        const newMessage: ThreadMessageLike = {
          id: event.data.id,
          role: "assistant" as const,
          content: [
            {
              type: "tool-call" as const,
              toolCallId: event.data.id,
              toolName: event.data.tool,
              args: event.data.args,
              argsText: JSON.stringify(event.data.args),
            },
          ],
          createdAt: new Date(),
        };

        return { messages: [...state.messages, newMessage] };
      }

      // Handle text events
      if (event.type === "text") {
        const content: Array<{ type: "text"; text: string } | { type: "image"; image: string }> = [
          {
            type: "text" as const,
            text: event.data.content,
          },
        ];

        // Add attachments if present
        if (event.data.attachments && event.data.attachments.length > 0) {
          for (const attachment of event.data.attachments) {
            if (attachment.type === "image") {
              content.push({
                type: "image" as const,
                image: attachment.url,
              });
            }
          }
        }

        const newMessage: ThreadMessageLike = {
          id: event.data.id,
          role: event.data.agent === "human" || event.data.agent === "user" ? "user" : "assistant",
          content,
          createdAt: new Date(),
        };

        const existingIndex = state.messages.findIndex((m) => m.id === newMessage.id);

        // If message exists, replace it (text event has higher priority than text_delta)
        if (existingIndex !== -1) {
          const updatedMessages = [...state.messages];
          updatedMessages[existingIndex] = newMessage;
          return { messages: updatedMessages };
        }
        return { messages: [...state.messages, newMessage] };
      }

      // Handle interrupt events
      if (event.type === "interrupt") {
        const newMessage: ThreadMessageLike = {
          id: `interrupt-${Date.now()}`,
          role: "assistant" as const,
          content: [
            {
              type: "tool-call" as const,
              toolCallId: `hitl-${Date.now()}`,
              toolName: "confirmationCard",
              args: {
                description: event.data.description,
                decisions: event.data.decisions,
                actionRequests: event.data.actionRequests || [],
              },
              argsText: JSON.stringify({
                description: event.data.description,
                decisions: event.data.decisions,
                actionRequests: event.data.actionRequests || [],
              }),
            },
          ],
          createdAt: new Date(),
        };
        return { messages: [...state.messages, newMessage] };
      }

      return state;
    });
  },

  addUserMessage: (message: ThreadMessageLike) => {
    set((state) => ({
      messages: [...state.messages, message],
      suggestions: [], // Clear suggestions when user sends message
    }));
  },

  addErrorMessage: (text: string) => {
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: `error-${Date.now()}`,
          role: "assistant" as const,
          content: [{ type: "text" as const, text: `❌ ${text}` }],
          createdAt: new Date(),
        },
      ],
    }));
  },

  setMessages: (messages: ThreadMessageLike[]) => {
    set({ messages });
  },

  setIsRunning: (running: boolean) => {
    set({ isRunning: running });
  },

  setThreadId: (id: string) => {
    set({ threadId: id });
  },

  setSuggestions: (suggestions: string[]) => {
    set({ suggestions });
  },

  resetThread: () => {
    set({
      messages: [],
      threadId: uuidv4(),
      isRunning: false,
      suggestions: [], // Clear suggestions on reset
    });
  },
}));
